/**
 * Subscribed services / organization type helpers for School forms and tables.
 *
 * Values are the UserService GraphQL enum names (UPPERCASE). Schools created before these
 * fields existed have no value in the database and are PikaDo English schools, so every
 * reader falls back to `['ENGLISH']` / `'SCHOOL'` instead of showing blanks.
 */

export type SubscribedService = 'ENGLISH' | 'SHIELD';
export type OrgType = 'SCHOOL' | 'NURSERY' | 'CENTER';

export const DEFAULT_SUBSCRIBED_SERVICES: SubscribedService[] = ['ENGLISH'];
export const DEFAULT_ORG_TYPE: OrgType = 'SCHOOL';

export const subscribedServiceOptions: Array<{
  label: string;
  value: SubscribedService;
}> = [
  {label: 'English (PikaDo)', value: 'ENGLISH'},
  {label: 'Shield', value: 'SHIELD'},
];

export const orgTypeOptions: Array<{label: string; value: OrgType}> = [
  {label: 'School', value: 'SCHOOL'},
  {label: 'Nursery', value: 'NURSERY'},
  {label: 'Center', value: 'CENTER'},
];

/**
 * Anything that may carry the two fields: a `SchoolProfile` from `@PikaDoApp/npm-pikado-logic`
 * (older package versions do not declare them yet), a raw GraphQL row, or nothing at all.
 */
type SchoolLike = object | null | undefined;

const readField = <T>(school: SchoolLike, field: string): T | undefined =>
  school
    ? ((school as Record<string, unknown>)[field] as T | undefined)
    : undefined;

/** Org type of a school, falling back to SCHOOL when the field is undefined (legacy document). */
export const resolveOrgType = (school: SchoolLike): OrgType =>
  readField<OrgType | null>(school, 'orgType') ?? DEFAULT_ORG_TYPE;

/** Subscribed services of a school, falling back to [ENGLISH] when the field is undefined. */
export const resolveSubscribedServices = (
  school: SchoolLike,
): SubscribedService[] => {
  const services = readField<SubscribedService[] | null>(
    school,
    'subscribedServices',
  );
  return Array.isArray(services) ? services : DEFAULT_SUBSCRIBED_SERVICES;
};

export const orgTypeLabel = (
  value: OrgType | string | null | undefined,
): string => {
  const resolved = value ?? DEFAULT_ORG_TYPE;
  return (
    orgTypeOptions.find(o => o.value === resolved)?.label ?? String(resolved)
  );
};

export const subscribedServiceLabel = (
  value: SubscribedService | string,
): string =>
  subscribedServiceOptions.find(o => o.value === value)?.label ?? String(value);
